import { type FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';

import { BsPlus } from 'react-icons/bs';
import { MdClear } from 'react-icons/md';

import ReactDataSheet from 'react-datasheet';
import 'react-datasheet/lib/react-datasheet.css';

import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type GeoMap } from '@editor2/types/pageElements';
import { getGeoMapInitColors } from '@editor2/services/geoMapService';

import ColorPickerCell from './ColorPickerCell';
import { regionDataStyle, addRowButton, deleteRowButton } from './styles';
import { type GridElement } from '@editor2/types/gridElement';

class MyReactDataSheet extends ReactDataSheet<GridElement, any> {}

interface Props {
	element: GeoMap;
	courseId: number;
}

const GroupsTable: FC<Props> = (props) => {
	const { element, courseId } = props;

	const [grid, setGrid] = useState([]);
	const dispatch = useDispatch();

	const commonPayload = { elementId: element.family_id, courseId };

	const GROUPS_COLUMNS = [
		{ name: 'Name', propertyName: 'name' },
		{ name: 'Color', propertyName: 'color' }
	];

	const onCellsChanged = (changes) => {
		let updatedColors = [...element.colors];

		changes.forEach(({ row, col, value }) => {
			updatedColors = [
				...updatedColors.slice(0, row - 1),
				{ ...updatedColors[row - 1], [GROUPS_COLUMNS[col].propertyName]: value },
				...updatedColors.slice(row)
			];
		});

		dispatch(updateElement({ ...commonPayload, colors: updatedColors }));
	};

	const onAddRowClick = () => {
		const newRow = getGeoMapInitColors();
		const updatedColors = [...element.colors, ...newRow];
		dispatch(updateElement({ ...commonPayload, colors: updatedColors }));
	};

	const onDeleteRowClick = (index) => {
		const groupToDeleteId = element.colors[index].id;
		const itemsWithGroupId = Object.keys(element.data).filter(
			(key) => element.data[key][0] === groupToDeleteId
		);

		if (
			!itemsWithGroupId.length ||
			(itemsWithGroupId.length > 0 &&
				// eslint-disable-next-line no-alert
				window.confirm(
					`Are you sure to delete the group? You have ${itemsWithGroupId.length} rows with this value.`
				))
		) {
			const updatedColors = [...element.colors.slice(0, index), ...element.colors.slice(index + 1)];

			const updatedData = Object.keys(element.data).reduce((acc, curr) => {
				const colorId = element.data[curr][0];
				return {
					...acc,
					[curr]: [colorId === groupToDeleteId ? '' : colorId, ...element.data[curr].slice(1)]
				};
			}, {});

			dispatch(updateElement({ ...commonPayload, colors: updatedColors, data: updatedData }));
		}
	};

	const handleColorChange = ({ index, value }) => {
		let updatedColors = [...element.colors];

		updatedColors = [
			...updatedColors.slice(0, index),
			{ ...updatedColors[index], color: value },
			...updatedColors.slice(index + 1)
		];

		dispatch(updateElement({ ...commonPayload, colors: updatedColors }));
	};

	const generateRow = ({ key, color, index }) => {
		const row = [
			{ value: key },
			{
				value: <ColorPickerCell defaultColor={color} index={index} onChange={handleColorChange} />,
				expr: color
			},
			{
				component: <MdClear className={deleteRowButton} onClick={() => onDeleteRowClick(index)} />,
				forceComponent: true
			}
		];

		return row;
	};

	const generateGrid = () => {
		const headerRow = [
			...GROUPS_COLUMNS.map((column) => ({ readOnly: true, value: column.name })),
			{ readOnly: true }
		];

		const rows = element.colors.map(({ name, color }, index) =>
			generateRow({ key: name, color, index })
		);

		return [headerRow, ...rows];
	};

	useEffect(() => {
		const rows = generateGrid();
		setGrid(rows);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [element]);

	return (
		<>
			<MyReactDataSheet
				className={regionDataStyle}
				data={grid}
				valueRenderer={(cell) => cell.value}
				dataRenderer={(cell) => cell.expr || cell.value}
				onCellsChanged={onCellsChanged}
			/>

			<Button
				className={addRowButton}
				variant="outline-secondary"
				size="sm"
				onClick={onAddRowClick}>
				<BsPlus /> add row
			</Button>
		</>
	);
};

export default GroupsTable;
